import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Grid } from '@mui/material';

import { isRtl } from './markets/default/i18n';
import { FullScreen, useFullScreenHandle } from './components/FullScreen';
import { useAnalytics } from './components/Analytics';
import { HeatMapExperience } from './components/HeatMapExperience';
import { NavigationBar } from './components/NavigationBar';
import {
  useNavigationBarData,
  useUpdateNavigationBar,
} from './components/NavigationBar/NavigationBarProvider';
import { theme } from './styles/theme';
import { useWindowSize } from './utils/UseWindowSize';
import { PointOfInterestExperience } from './components/PointOfInterestExperience';
import { useComparisonProviderContext } from './providers/ComparisonProvider';
import { useVirtualTour } from './components/VirtualTour';
import { useMapData } from './map-data/use-map-data';
import { TourUpdateTab } from './components/VirtualTour/TourUpdateTab';
import { WelcomeModal } from './components/VirtualTour/WelcomeModal';
import { ComparisonTool } from './components/ComparisonTool';
import { FavouritesButton, FavouritesDrawer } from './components/Favourites';

const MapLayoutContainer = styled.main`
  background-color: #f0f0f0;
  display: flex;
  height: 100vh;
  width: 100vw;
  max-width: 100%;
  flex-direction: column-reverse;
  box-sizing: border-box;
  overflow-x: hidden;
  @media (min-width: ${theme.breakPoints.desktop}px) {
    flex-direction: column;
  }
`;

const MapHeader = styled.div`
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  box-sizing: border-box;
`;

export function App() {
  const { selectedNavigationItem, selectedExperience, setSelectedExperience } =
    useNavigationBarData();
  const { loading: mapDataLoading } = useMapData();
  const [favouritesDrawerOpen, setFavouritesDrawerOpen] = useState(false);

  const { updateSelectedNavigationItem } = useUpdateNavigationBar();

  const { i18n, t } = useTranslation();
  useAnalytics();
  const handle = useFullScreenHandle(document.querySelector('body'));

  const size = useWindowSize();

  const {
    heatMapComparison,
    poiComparison,
    setHeatMapComparison,
    setPoiComparison,
  } = useComparisonProviderContext();

  const { showWelcomeModal, handleWelcomeSelection } = useVirtualTour();

  useEffect(() => {
    if (
      size.width! < theme.breakPoints.desktop &&
      selectedNavigationItem === 'comparison'
    ) {
      updateSelectedNavigationItem(undefined);
    }
  }, [selectedNavigationItem, size, updateSelectedNavigationItem]);

  const isPinMap = selectedExperience !== 'heatMap';
  const isComparisonDisplay = selectedNavigationItem === 'comparison';

  // handle setting the experience based on URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('poiId')) setSelectedExperience('pinMap');
    if (params.get('heatmapId')) setSelectedExperience('heatMap');
    if (params.get('compareHeatMap')) {
      const items = params.get('compareHeatMap')!.split(',');

      if (items.length >= 2 && items.length <= 4) {
        setHeatMapComparison({
          display: true,
          countryCodes: items,
          syncVersion: Date.now(),
        });
        updateSelectedNavigationItem('comparison');
        setSelectedExperience('heatMap');
      }
    }
    if (params.get('comparePoi')) {
      const items = params.get('comparePoi')!.split(',');

      if (items.length >= 2 && items.length <= 4) {
        setPoiComparison({
          display: true,
          studyIds: items,
          syncVersion: Date.now(),
        });
        updateSelectedNavigationItem('comparison');
        setSelectedExperience('pinMap');
      }
    }
  }, [
    setHeatMapComparison,
    setPoiComparison,
    setSelectedExperience,
    updateSelectedNavigationItem,
  ]);

  if (mapDataLoading) {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  // Loading the data set into the map is expensive.
  // The code below ensures both maps are loaded by
  // default, and then when once becomes visible it
  // gives the map a nudge to ensure it's correctly
  // sized.

  // Detect - fullscreen API and close button
  return (
    <>
      <Helmet
        titleTemplate="%s - LCPN Interactive Map"
        defaultTitle="LCPN Interactive Map"
        htmlAttributes={{
          lang: i18n.language,
          dir: isRtl(i18n.language) ? 'rtl' : 'ltr',
        }}
      >
        <meta
          name="description"
          content={t('global.meta.description', {
            defaultValue: 'LCPN interactive map',
          })}
        />
      </Helmet>
      <WelcomeModal
        open={showWelcomeModal}
        onSelectView={handleWelcomeSelection}
      />
      <FullScreen handle={handle}>
        <MapLayoutContainer id="map-full-screen-container">
          <MapHeader>
            <NavigationBar
              onFavouritesClick={() => setFavouritesDrawerOpen(true)}
              fullScreenHandle={handle}
            />
          </MapHeader>
          {isComparisonDisplay && (
            <ComparisonTool
              isPinMapView={isPinMap}
              showHeatMapTable={!!heatMapComparison.display}
              showPoiTable={!!poiComparison.display}
            />
          )}
          {!isComparisonDisplay && <TourUpdateTab />}
          {isComparisonDisplay && (
            <FavouritesButton onClick={() => setFavouritesDrawerOpen(true)} />
          )}
          <FavouritesDrawer
            open={favouritesDrawerOpen}
            onClose={() => setFavouritesDrawerOpen(false)}
          />
          <HeatMapExperience visible={!isPinMap && !isComparisonDisplay} />
          <PointOfInterestExperience
            visible={isPinMap && !isComparisonDisplay}
          />
        </MapLayoutContainer>
      </FullScreen>
    </>
  );
}
